<script setup lang="ts">
import { computed } from 'vue'
import {
  SeatingPlanFieldsFragment,
  HeadingFieldsFragment,
  RoomItemFieldsFragment,
  MediaImageFieldsFragment,
} from '~/api/graphql/generated'

const props = defineProps<{
  entity: SeatingPlanFieldsFragment
}>()

const headingData = computed(() => props.entity.fieldHeading?.entity as HeadingFieldsFragment | undefined)

const roomItems = computed(
  () =>
    props.entity.fieldItems
      ?.map((item) => {
        const roomData = item?.entity as RoomItemFieldsFragment | undefined
        if (!roomData) return null
        return {
          image: roomData.fieldFloorPlanImage?.entity as MediaImageFieldsFragment,
          title: roomData.fieldTitleFormatted?.value || '',
          subtitle: roomData.fieldSubtitle || '',
          table: roomData.fieldTable?.value || '',
          reference: roomData.fieldText?.value || '',
        }
      })
      .filter(Boolean) || [],
)
</script>

<template>
  <Section v-animate="{ cluster: true }">
    <Heading
      v-if="headingData"
      v-animate="{ animation: 'vertical-reveal' }"
      :data="headingData"
      class="heading-title t-10 t-o-1 tl-7 tl-o-0 ds-o-1"
    />

    <!-- Room Items -->
    <ul class="room-items cul">
      <li v-for="(room, index) in roomItems" :key="index" class="room-item">
        <div class="room-content grid">
          <!-- Floor Plan Image -->
          <div v-if="room.image" v-animate="{ animation: 'vertical-reveal' }" class="room-image tl-6 ds-5">
            <Image :entity="room.image" type="seating-plan" />
          </div>

          <!-- Room Details -->
          <div v-animate="{ animation: 'vertical-reveal' }" class="room-details tl-6 ds-6 ds-o-1">
            <!-- Room Title -->
            <h3 v-if="room.title" class="room-title" v-html="room.title" />

            <!-- Room Subtitle -->
            <h4 v-if="room.subtitle" class="room-subtitle">
              {{ room.subtitle }}
            </h4>

            <!-- Seating Table -->
            <div class="room-table">
              <TableWithFade v-if="room.table" :content="room.table" class="text-xsmall" />
            </div>

            <!-- Reference Text -->
            <div v-if="room.reference" class="room-reference text-xsmall" v-html="room.reference" />
          </div>
        </div>
      </li>
    </ul>
  </Section>
</template>

<style scoped lang="scss">
.heading-title {
  @include fluid(margin-bottom, 36px, 48px);
}

.room-item {
  border-top: 1px solid var(--c-line-light);
  padding-top: 24px;
  margin-bottom: 48px;

  &:last-child {
    margin-bottom: 0;
  }
}
.room-title {
  @include fluid(margin-bottom, 36px, 48px);
}

.room-subtitle {
  @include fluid(margin-bottom, 18px, 24px);
}
.room-table {
  @include fluid(margin-bottom, 36px, 48px);

  :deep(table) {
    // min-width: 640px;
    border-collapse: collapse;

    th,
    td {
      padding: 8px 16px 8px 0;
      text-align: left;
      border: none;
      vertical-align: top;
    }

    th {
      color: var(--c-primary);
      font-weight: 600;
      text-transform: uppercase;
      padding-bottom: 8px;
    }

    td {
      color: var(--c-text);
      padding-top: 8px;
      min-width: 160px;
    }

    tr:not(:last-child) td {
      padding-bottom: 8px;
    }
  }
}

.room-reference {
  color: var(--c-text-medium);

  :deep(em) {
    font-style: italic;
  }
}
</style>
